import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { transportCustomRequestsService } from "@/services/system-admin/transport-custom-requests.service";
import type {
  TransportCustomRequestsState,
  GetCustomRequestsParams,
  RequestInformationRequest,
  AssignVehicleRequest,
  RejectCustomRequestRequest,
  WithdrawQuoteRequest,
  CreateQuoteRequest,
} from "@/types/admin/transport-custom-request-type";

const initialState: TransportCustomRequestsState = {
  requests: [],
  totalCount: 0,
  currentPage: 1,
  pageSize: 20,
  loading: false,
  error: null,

  requestDetail: null,
  detailLoading: false,

  actionLoading: false,
};

export const fetchCustomRequests = createAsyncThunk(
  "transportCustomRequests/fetchCustomRequests",
  async (params: GetCustomRequestsParams) => {
    const response = await transportCustomRequestsService.getRequests(params);
    return {
      ...response,
      page: params.page ?? 1,
      pageSize: params.pageSize ?? 20,
    };
  },
);

export const fetchCustomRequestDetail = createAsyncThunk(
  "transportCustomRequests/fetchCustomRequestDetail",
  async (requestUid: string) =>
    transportCustomRequestsService.getRequestById(requestUid),
);

export const requestCustomRequestInfo = createAsyncThunk(
  "transportCustomRequests/requestCustomRequestInfo",
  async ({
    requestUid,
    payload,
  }: {
    requestUid: string;
    payload: RequestInformationRequest;
  }) => transportCustomRequestsService.requestInformation(requestUid, payload),
);

export const assignCustomRequestVehicle = createAsyncThunk(
  "transportCustomRequests/assignCustomRequestVehicle",
  async ({
    requestUid,
    payload,
  }: {
    requestUid: string;
    payload: AssignVehicleRequest;
  }) => transportCustomRequestsService.assignVehicle(requestUid, payload),
);

export const createCustomRequestQuote = createAsyncThunk(
  "transportCustomRequests/createCustomRequestQuote",
  async ({
    requestUid,
    payload,
  }: {
    requestUid: string;
    payload: CreateQuoteRequest;
  }) => transportCustomRequestsService.createQuote(requestUid, payload),
);

export const updateCustomRequestQuote = createAsyncThunk(
  "transportCustomRequests/updateCustomRequestQuote",
  async ({
    requestUid,
    quoteUid,
    payload,
  }: {
    requestUid: string;
    quoteUid: string;
    payload: CreateQuoteRequest;
  }) => transportCustomRequestsService.updateQuote(requestUid, quoteUid, payload),
);

export const withdrawCustomRequestQuote = createAsyncThunk(
  "transportCustomRequests/withdrawCustomRequestQuote",
  async ({
    requestUid,
    quoteUid,
    payload,
  }: {
    requestUid: string;
    quoteUid: string;
    payload: WithdrawQuoteRequest;
  }) => transportCustomRequestsService.withdrawQuote(requestUid, quoteUid, payload),
);

export const rejectCustomRequest = createAsyncThunk(
  "transportCustomRequests/rejectCustomRequest",
  async ({
    requestUid,
    payload,
  }: {
    requestUid: string;
    payload: RejectCustomRequestRequest;
  }) => transportCustomRequestsService.rejectRequest(requestUid, payload),
);

const transportCustomRequestsSlice = createSlice({
  name: "transportCustomRequests",
  initialState,
  reducers: {
    setPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
    },
    clearRequestDetail: (state) => {
      state.requestDetail = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ---- list ----
      .addCase(fetchCustomRequests.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.requests = action.payload.items;
        state.totalCount = action.payload.totalCount;
        state.currentPage = action.payload.page;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchCustomRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to fetch custom requests.";
      })

      // ---- detail ----
      .addCase(fetchCustomRequestDetail.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomRequestDetail.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.requestDetail = action.payload;
      })
      .addCase(fetchCustomRequestDetail.rejected, (state, action) => {
        state.detailLoading = false;
        state.error = action.error.message || "Failed to fetch request.";
      })

      // ---- generic action matchers ----
      .addMatcher(
        (action) =>
          action.type.startsWith("transportCustomRequests/") &&
          action.type.endsWith("/pending") &&
          !action.type.includes("fetchCustomRequests") &&
          !action.type.includes("fetchCustomRequestDetail"),
        (state) => {
          state.actionLoading = true;
          state.error = null;
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("transportCustomRequests/") &&
          action.type.endsWith("/fulfilled") &&
          !action.type.includes("fetchCustomRequests") &&
          !action.type.includes("fetchCustomRequestDetail"),
        (state, action: PayloadAction<any>) => {
          state.actionLoading = false;
          if (action.payload) {
            state.requestDetail = action.payload;
          }
        },
      )
      .addMatcher(
        (action) =>
          action.type.startsWith("transportCustomRequests/") &&
          action.type.endsWith("/rejected") &&
          !action.type.includes("fetchCustomRequests") &&
          !action.type.includes("fetchCustomRequestDetail"),
        (state, action: any) => {
          state.actionLoading = false;
          state.error = action.error?.message || "Action failed.";
        },
      );
  },
});

export const { setPage, setPageSize, clearRequestDetail, clearError } =
  transportCustomRequestsSlice.actions;
export default transportCustomRequestsSlice.reducer;